import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Lock, Unlock } from "lucide-react";
import styled, { keyframes, css } from "styled-components";
import useNotificationStore from "../services/notificationStore";
import { myLocalStorage } from "../components/StorageHelper";


import Logo from "../vendor/swan.png";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
`;

const SIDEBAR_EXPANDED_WIDTH = "248px";
const SIDEBAR_COLLAPSED_WIDTH = "72px";

const SidebarWrapper = styled.div`
  position: relative;
  height: 100vh;
  display: flex;
  flex-shrink: 0;
`;

const NotificationBadge = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: ${({ $active }) => ($active ? "white" : "hsl(var(--primary))")};
  color: ${({ $active }) => ($active ? "hsl(var(--primary))" : "white")};
  font-size: 11px;
  font-weight: 700;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ $active }) =>
    $active
      ? "0 1px 3px rgba(0, 0, 0, 0.15)"
      : "0 1px 3px hsl(var(--primary) / 0.3)"};
  margin-left: 8px;
  flex-shrink: 0;
  transition: all 0.2s ease;
`;


const SidebarContainer = styled.nav`
  width: ${({ $expanded }) =>
    $expanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH};
  height: 100%;
  background: hsl(var(--sidebar-bg));
  border-right: 1px solid hsl(var(--border));
  border-top-right-radius: 24px;
  border-bottom-right-radius: 24px;
  box-shadow: ${({ $expanded }) =>
    $expanded
      ? "4px 0 24px rgba(0, 0, 0, 0.08)"
      : "2px 0 8px rgba(0, 0, 0, 0.04)"};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 50;
`;

const LogoSection = styled.div`
  padding: ${({ $expanded }) => ($expanded ? "24px 16px" : "20px 16px")};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 14px;
  border-bottom: 1px solid hsl(var(--border));
  min-height: 80px;
  transition: padding 0.3s ease;

  img {
    width: px;
    height: 40px;
    object-fit: contain;
    flex-shrink: 0;
    transition: none;
  }

  .logo-text {
    display: ${({ $expanded }) => ($expanded ? "flex" : "none")};
    flex-direction: column;
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    transition: all 0.3s ease;
    white-space: nowrap;

    .brand-name {
      font-weight: 700;
      font-size: 16px;
      color: hsl(var(--foreground));
      letter-spacing: 0.5px;
    }

    .brand-tagline {
      font-size: 10px;
      color: hsl(var(--muted-foreground));
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-top: 2px;
    }
  }
`;

const MenuList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;

  /* Hide scrollbar but keep functionality */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const MenuItem = styled.div`
  position: relative;
  margin-bottom: 4px;
`;

const MenuButton = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  gap: 14px;
  cursor: pointer;
  color: hsl(var(--muted-foreground));
  border-radius: 12px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  white-space: nowrap;
  min-height: 48px;
  position: relative;

  &:hover {
    background: hsl(var(--accent));
    color: hsl(var(--primary));
    transform: ${({ $expanded }) => ($expanded ? "translateX(4px)" : "scale(1.05)")};

    .icon {
      color: hsl(var(--primary));
      transform: scale(1.1);
    }
  }

  &.active {
    background: hsl(var(--primary));
    color: hsl(var(--primary-foreground));
    box-shadow: 0 4px 12px hsl(var(--primary) / 0.3);

    .icon {
      color: hsl(var(--primary-foreground));
    }

    .label {
      font-weight: 600;
    }

    .chevron {
      color: hsl(var(--primary-foreground));
    }
  }

  .icon {
    width: 24px;
    height: 24px;
    min-width: 24px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    color: #64748b;

    svg {
      width: 22px;
      height: 22px;
    }
  }

.label {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  display: ${({ $expanded }) => ($expanded ? "inline-flex" : "none")};
  align-items: center;
  gap: 8px;
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  transition: all 0.25s ease;
  ${({ $expanded }) =>
    $expanded &&
    css`
      animation: ${fadeIn} 0.3s ease forwards;
    `}
  }


  .chevron {
    display: ${({ $expanded }) => ($expanded ? "flex" : "none")};
    opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
    transition: all 0.2s ease;
    color: hsl(var(--muted-foreground));
  }
`;

const SubMenu = styled.div`
  overflow: hidden;
  max-height: ${({ $open, $expanded }) => ($open && $expanded ? "500px" : "0")};
  opacity: ${({ $open, $expanded }) => ($open && $expanded ? 1 : 0)};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-left: 20px;
  margin-top: ${({ $open, $expanded }) => ($open && $expanded ? "4px" : "0")};
  border-left: 2px solid hsl(var(--border));
  padding-left: 12px;
  display: ${({ $expanded }) => ($expanded ? "block" : "none")};
`;

const SubMenuItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
  text-decoration: none;
  border-radius: 8px;
  margin-bottom: 2px;
  transition: all 0.2s ease;
  position: relative;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .sub-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sub-icon {
    flex-shrink: 0;
    margin-left: 8px;
    opacity: 0.7;
  }

  &::before {
    content: "";
    position: absolute;
    left: -14px;
    top: 50%;
    width: 8px;
    height: 2px;
    background: hsl(var(--border));
    transition: all 0.2s ease;
  }

  &:hover {
    color: hsl(var(--primary));
    background: hsl(var(--accent));
    transform: translateX(4px);

    &::before {
      background: hsl(var(--primary));
      width: 12px;
    }
  }

  &.active {
    color: hsl(var(--primary));
    background: hsl(var(--primary) / 0.1);

    &::before {
      background: hsl(var(--primary));
      width: 12px;
    }
  }
`;

// ========== NEW: Pin Section Styled Components ==========

const PinSection = styled.div`
  padding: 16px;
  border-top: 1px solid hsl(var(--border));
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background: hsl(var(--sidebar-bg));
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  pointer-events: ${({ $expanded }) => ($expanded ? "auto" : "none")};
  transition: opacity 0.15s ease
    ${({ $expanded }) => ($expanded ? "0.18s" : "0s")};
`;

const ToggleSwitch = styled.button`
  position: relative;
  width: 52px;
  height: 28px;
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.8))"
      : "hsl(var(--muted))"};
  border-radius: 14px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${({ $active }) =>
    $active
      ? "0 0 20px hsl(var(--primary) / 0.4), inset 0 1px 1px rgba(255,255,255,0.2)"
      : "inset 0 2px 4px rgba(0,0,0,0.1)"};
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
    box-shadow: ${({ $active }) =>
    $active
      ? "0 0 24px hsl(var(--primary) / 0.5), inset 0 1px 1px rgba(255,255,255,0.2)"
      : "inset 0 2px 6px rgba(0,0,0,0.15)"};
  }

  &:active {
    transform: scale(0.98);
  }

  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: ${({ $active }) => ($active ? "26px" : "3px")};
    width: 22px;
    height: 22px;
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 50%;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .toggle-icon {
    position: absolute;
    top: 50%;
    left: ${({ $active }) => ($active ? "26px" : "3px")};
    transform: translate(calc(11px - 50%), -50%);
    width: 14px;
    height: 14px;
    color: ${({ $active }) =>
    $active ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const PinLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $active }) =>
    $active ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"};
  display: ${({ $expanded }) => ($expanded ? "block" : "none")};
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  transition: all 0.3s ease;
  white-space: nowrap;
  ${({ $expanded }) =>
    $expanded &&
    css`
      animation: ${fadeIn} 0.3s ease forwards;
    `}
`;

// ========== END: Pin Section Styled Components ==========

const ANALYTICS_LINK_PREFIX = "cloud-dashboard-analytics-link";

const cloudProviderByMenuHref = {
  "/aws": "aws",
  "/azure-directory": "azure",
  "/gcp": "gcp",
};

const parseSearchParams = (search) => {
  try {
    return new URLSearchParams(search || "");
  } catch {
    return new URLSearchParams();
  }
};

const normalizeCloudProvider = (provider) => {
  const normalized = String(provider || "").toLowerCase();
  if (normalized === "aws") return "aws";
  if (normalized === "azure" || normalized === "az") return "azure";
  if (normalized === "gcp" || normalized === "google") return "gcp";
  return "";
};

const CollapsibleSidebar = ({ menuList }) => {
  const [expanded, setExpanded] = useState(true);
  const [openGroups, setOpenGroups] = useState({});
  const [isPinned, setIsPinned] = useState(true);
  const location = useLocation();
  const totalPendingCount = useNotificationStore((state) => state.totalPendingCount);

  const toggleGroup = (groupId, children) => {
    setOpenGroups((prev) => {
      const isCurrentlyOpen =
        prev[groupId] !== undefined ? prev[groupId] : hasActiveChild(children);

      if (!isCurrentlyOpen) {
        // Open this group and set a flag to force others to close
        return { [groupId]: true, _userToggled: true };
      } else {
        // Close this group
        return { [groupId]: false, _userToggled: true };
      }
    });
  };

  const getCloudEntityAnalyticsProvider = () => {
    if (location.pathname !== "/dashboard/cloud-entity-analytics") {
      return "";
    }

    const searchParams = parseSearchParams(location.search);
    const explicitProvider =
      searchParams.get("cloudProvider") ||
      searchParams.get("cloudType") ||
      location.state?.cloudProvider ||
      location.state?.cloudType;
    const normalizedExplicitProvider = normalizeCloudProvider(explicitProvider);
    if (normalizedExplicitProvider) {
      return normalizedExplicitProvider;
    }

    const linkId = searchParams.get("linkId");
    const payload = linkId
      ? myLocalStorage.getItem(`${ANALYTICS_LINK_PREFIX}:${linkId}`)
      : null;

    return normalizeCloudProvider(payload?.cloudProvider || payload?.cloudType);
  };

  const cloudEntityAnalyticsProvider = getCloudEntityAnalyticsProvider();

  const pathMatches = (path) =>
    Boolean(path) &&
    (location.pathname === path || location.pathname.startsWith(`${path}/`));

  const isCloudProviderActive = (path) =>
    cloudEntityAnalyticsProvider &&
    cloudProviderByMenuHref[path] === cloudEntityAnalyticsProvider;

  const isActive = (path) => pathMatches(path) || isCloudProviderActive(path);

  const childMatches = (child) =>
    pathMatches(child.href) ||
    (child.matchPaths || []).some((path) => pathMatches(path));

  const hasActiveChild = (children) =>
    children?.some((child) => isActive(child.href) || childMatches(child));

  const renderIcon = (IconComponent) => {
    if (!IconComponent) return null;

    try {
      if (typeof IconComponent === "function") {
        return <IconComponent size={22} />;
      }

      if (typeof IconComponent === "object" && IconComponent !== null) {
        if (IconComponent.muiName) {
          return <IconComponent sx={{ fontSize: 22 }} />;
        }
        return (
          <IconComponent
            size={22}
            sx={{ fontSize: 22 }}
            style={{ fontSize: 22, width: 22, height: 22 }}
          />
        );
      }

      return null;
    } catch (error) {
      console.error("Error rendering icon:", error);
      return null;
    }
  };

  const renderMenuItem = (item, index) => {
    const hasChildren = item.children && item.children.length > 0;
    const isCurrentlyActiveChild = hasActiveChild(item.children) || isActive(item.href);

    // Evaluate if the menu should be open
    const isOpen =
      openGroups[item.title] !== undefined
        ? openGroups[item.title]
        : openGroups._userToggled
          ? false // If the user clicked to toggle something, default un-interacted groups to closed
          : isCurrentlyActiveChild; // Otherwise, default to open if a child is active

    const IconComponent = item.icon;

    // Check if this is the "Resource Requests" menu item
    const isResourceRequest = item.title === "Resource Requests";
    const showBadge = isResourceRequest && totalPendingCount > 0;

    // Determine if this menu item is currently active
    const isMenuActive = hasChildren
      ? isCurrentlyActiveChild
      : isActive(item.href);

    if (hasChildren) {
      return (
        <MenuItem key={item.title}>
          <MenuButton
            $expanded={expanded}
            onClick={() => expanded && toggleGroup(item.title, item.children)}
            className={isMenuActive ? "active" : ""}
          >
            <span className="icon">{renderIcon(IconComponent)}</span>
            <span className="label">
              {item.title}
              {item.newTag && (
                <span style={{
                  marginLeft: 6,
                  padding: "1px 5px",
                  fontSize: 8,
                  fontWeight: 600,
                  letterSpacing: "0.4px",
                  lineHeight: "13px",
                  borderRadius: 3,
                  background: "#126cff",
                  color: "#fff",
                  textTransform: "uppercase",
                  verticalAlign: "top",
                  position: "relative",
                  top: -2,
                  whiteSpace: "nowrap",
                }}>
                  New
                </span>
              )}
              {showBadge && (
                <NotificationBadge $active={isMenuActive}>
                  {totalPendingCount > 99 ? "99+" : totalPendingCount}
                </NotificationBadge>
              )}
            </span>
            <span className="chevron">
              {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          </MenuButton>
          <SubMenu $open={isOpen} $expanded={expanded}>
            {item.children.map((child) => {
              const ChildIcon = child.icon;
              const childContent = (
                <>
                  <span className="sub-label">{child.title}</span>
                  {ChildIcon && <ChildIcon size={14} className="sub-icon" />}
                </>
              );

              if (child.external) {
                return (
                  <SubMenuItem
                    key={child.href}
                    as="a"
                    href={child.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {childContent}
                  </SubMenuItem>
                );
              }

              return (
                <SubMenuItem
                  key={child.href}
                  to={child.href}
                  className={childMatches(child) ? "active" : ""}
                >
                  {childContent}
                </SubMenuItem>
              );
            })}
          </SubMenu>
        </MenuItem>
      );
    }

    return (
      <MenuItem key={item.title}>
        <MenuButton
          as={NavLink}
          to={item.href}
          $expanded={expanded}
          className={isActive(item.href) ? "active" : ""}
          onClick={() => {
            if (expanded) {
              setOpenGroups({ _userToggled: true });
            }
          }}
        >
          <span className="icon">{renderIcon(IconComponent)}</span>
          <span className="label">
            {item.title}
            {showBadge && (
              <NotificationBadge $active={isActive(item.href)}>
                {totalPendingCount > 99 ? "99+" : totalPendingCount}
              </NotificationBadge>
            )}
          </span>
        </MenuButton>
      </MenuItem>
    );
  };


  return (
    <SidebarWrapper
      onMouseEnter={() => {
        setExpanded(true);
        if (!isPinned) {
          setOpenGroups({});
        }
      }}
      onMouseLeave={() => {
        if (!isPinned) {
          setExpanded(false);
          setOpenGroups({});
        }
      }}
    >
      <SidebarContainer $expanded={expanded}>
        <LogoSection $expanded={expanded}>
          <img src={Logo} alt="WhiteSwan Logo" />
          <div className="logo-text">
            <span className="brand-name">WHITESWAN</span>
            <span className="brand-tagline">Identity Security</span>
          </div>
        </LogoSection>
        <MenuList $expanded={expanded}>
          {menuList?.map((section, idx) => (
            <div key={section.title || section.href || idx}>
              {section.pages
                ? section.pages.map((item, i) => renderMenuItem(item, i))
                : renderMenuItem(section, idx)}
            </div>
          ))}
        </MenuList>

        {/* Pin/Lock Toggle Section */}
        <PinSection $expanded={expanded}>
          <ToggleSwitch
            $active={isPinned}
            onClick={() => setIsPinned(!isPinned)}
            aria-label={isPinned ? "Unpin sidebar" : "Pin sidebar"}
          >
            <span className="toggle-icon">
              {isPinned ? <Lock size={12} /> : <Unlock size={12} />}
            </span>
          </ToggleSwitch>
        </PinSection>
      </SidebarContainer>
    </SidebarWrapper>
  );
};

export default CollapsibleSidebar;
